import { useContext, useEffect, useRef, useState } from 'react';
import { onIdTokenChanged } from 'firebase/auth';

import { useAuth } from '../firebase/FirebaseContext';
import { hasPendingAuthRedirect } from './accountAuth';
import { AuthGateContext } from './AuthGateContext';
import { useMergeStatus } from './MergeStateContext';

/**
 * "Auth is in flux" — true when a sign-in / link / merge / uid-swap is in
 * progress or has just completed. Consumers use this to defer "no doc"
 * empty-state UIs until listeners have had a chance to re-bind under the
 * new auth state.
 *
 * Why this matters:
 * - On the cross-account merge path, `mergeStatus` is the canonical signal,
 *   but it only flips during the active merge. The post-merge propagation
 *   window (Firestore listeners reattaching, snapshot round-trip) leaves
 *   `mergeStatus === 'merged'` while the page briefly sees `!doc`.
 * - On the link-redirect happy path (anon uid is upgraded in-place), there
 *   is no merge, so `mergeStatus` stays 'idle', but `currentUser` flips
 *   `isAnonymous: true → false` and `onIdTokenChanged` fires. During that
 *   beat the page can also flash an empty state if a snapshot rate-races
 *   the token refresh.
 * - On the mobile-OAuth return leg, the page mounts BEFORE any of the above:
 *   Firebase restores the persisted ANON session first, and the permanent
 *   user only lands when `getRedirectResult` finalizes (~1-2s network
 *   round-trip). The redirect marker (`hasPendingAuthRedirect`) is the
 *   only signal that a flip is coming, so a marker at mount starts the
 *   grace window immediately — otherwise the header/boards render the anon
 *   "signed out" state and the sign-in looks like it didn't take.
 *
 * The hook combines these signals plus a small grace window after each
 * fires, so consumers can render a spinner during the entire transition
 * instead of a misleading "we couldn't find this" message. Every path is
 * bounded by `graceMs` — a hung `getRedirectResult` or merge can delay the
 * real UI by at most one grace window, never wedge it (same defense-in-depth
 * stance as `ensureAuth`'s per-step timeouts in firebaseConfig).
 */
export function useAuthInFlux(graceMs = 4000): boolean {
  const auth = useAuth();
  const mergeStatus = useMergeStatus();
  // Single settle signal for the redirect-return window: when an
  // AuthGateProvider is mounted (all app surfaces), its `redirectPending`
  // holds flux until `consumeAuthRedirectResult` ACTUALLY settles — capped
  // by the provider's safety timeout — so every consumer (header, CTAs,
  // homepage skeleton) releases at the same moment instead of racing a
  // fixed grace timer against the real network round-trip. The mount-time
  // marker peek below remains as the degraded fallback for gate-less hosts
  // (isolated tests/stories), where it self-expires after `graceMs`.
  const gate = useContext(AuthGateContext);
  const redirectPending = gate?.redirectPending ?? false;
  // Lazy-init from the redirect marker so the VERY FIRST render on a
  // post-redirect mount is already "in flux" — an effect would run after
  // paint and let one frame of anon UI through.
  const [recent, setRecent] = useState<boolean>(() => hasPendingAuthRedirect());
  const prevUidRef = useRef<string | null>(auth.currentUser?.uid ?? null);
  const prevAnonRef = useRef<boolean | null>(
    auth.currentUser?.isAnonymous ?? null,
  );
  // False when the hook mounted before Firebase resolved the persisted
  // session (currentUser still null). The first listener fire in that
  // state is initialization — restoring the cached user or minting the
  // anon session — not a transition, and must not start a grace window:
  // treating it as flux would flash a placeholder on EVERY cold anon load.
  const authResolvedRef = useRef<boolean>(auth.currentUser !== null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const startGrace = (): void => {
    setRecent(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setRecent(false);
      timerRef.current = null;
    }, graceMs);
  };

  // Arm the expiry timer for a mount-time pending redirect (the lazy state
  // init above can't start timers). If the credential never lands — redirect
  // lost to storage partitioning, user hit Back — the window expires and the
  // normal anon UI plus the AuthGate `redirectFailed` affordance take over.
  useEffect(() => {
    if (recent) startGrace();
    // Mount-only: `recent` here is deliberately the initial marker peek.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auth-state changes: uid swap (cross-account merge) or anon-flip
  // (linkWithRedirect / linkWithPopup upgrading an anon user in place).
  // onIdTokenChanged fires for both, plus token refreshes — we filter so
  // refreshes alone don't trigger the spinner.
  useEffect(() => {
    const unsub = onIdTokenChanged(auth, (user) => {
      const nextUid = user?.uid ?? null;
      const nextAnon = user?.isAnonymous ?? null;
      if (!authResolvedRef.current) {
        // Initial session resolution, not a transition — record silently.
        authResolvedRef.current = true;
        prevUidRef.current = nextUid;
        prevAnonRef.current = nextAnon;
        return;
      }
      const uidChanged = nextUid !== prevUidRef.current;
      const anonChanged = nextAnon !== prevAnonRef.current;
      if (!uidChanged && !anonChanged) return;
      // No user → anonymous user is `ensureAuth` minting the first-visit
      // anon session — bootstrap, not a transition. (Real sign-in flows
      // never go null → anon: link upgrades in place, cross-account swaps
      // to a permanent uid.)
      const mintedAnon = prevUidRef.current === null && nextAnon === true;
      prevUidRef.current = nextUid;
      prevAnonRef.current = nextAnon;
      if (mintedAnon) return;
      startGrace();
    });
    return () => {
      unsub();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);

  // Merge-state lifecycle: cover both the active merge and the propagation
  // window after it completes.
  useEffect(() => {
    if (mergeStatus === 'merging' || mergeStatus === 'merged') {
      startGrace();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mergeStatus]);

  return recent || redirectPending || mergeStatus === 'merging';
}
